import { useMemo } from 'react';
import { Clock, User, Package, Edit3, CheckCircle, AlertTriangle, Pause, FileText, PlusCircle, XCircle, Ban, Gavel, ShieldCheck, CheckCheck, RotateCcw } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { formatQuantityMentions, formatQuantityTokens, formatQuantityValue, formatStoredQuantities } from './quantityText';
import { CUSTOMER_REQUIRED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE } from '../../config/dateLabels';
import { closedRequestOutcome, isClosedRequestRow } from '../purchasing/changelogFields';
import {
  ACK_STATUS_DISPLAY, DECISION_OUTCOME_LABEL, SCHEDULE_ACK_STATUS_DISPLAY, SCHEDULE_DECISION_OUTCOME_LABEL, getEventKey,
} from './changelogLabels';

export interface ChangelogEntry {
  id: number;
  change_date: string;
  changed_by: string;
  changed_by_company?: string;
  // CR-4713: When the action was performed via impersonation, these surface
  // the real admin user (and their company) alongside the impersonated
  // changed_by. acting_as_user_details is the nested user object from the
  // backend's UserSerializer; acting_as_company is the resolved name.
  acting_as_user_details?: { id: number; first_name?: string; last_name?: string; full_name?: string; email?: string } | null;
  acting_as_company?: string | null;
  // The side of the table the row was written for (the impersonated session's
  // role when an admin acted). Not read by the attribution line: since
  // EXPEDITING-3 changed_by already names the person who actually acted.
  company_type?: 'customer' | 'supplier' | '';
  field_changed: string;
  old_value: string | null;
  new_value: string;
  reason?: string;
  change_type?: string;
  event_type?: string;
  // CR-4865: shared across rows that belong to the same PO change request
  // (proposal + decision). Null for pre-CR-4865 rows — the change-request
  // tab falls back to the legacy ungrouped renderer for those.
  change_request_ref?: number | null;
  // CR-4731 round 5: when the Audit History collapses a propose+decision
  // pair into a single row, the surviving row carries the submission
  // event metadata so the rendered card can show "Submitted by X on Y"
  // alongside the outcome. Sarah's complaint on PRO00176 line 1 was that
  // the audit was "showing the outcome but not the submission" — keeping
  // one row but surfacing both events resolves the contradiction with
  // the round 3 ask of "1 entry per change request".
  submission_event?: {
    change_date: string;
    changed_by: string;
    changed_by_company?: string;
  } | null;
  /** Customer review 11 Sep (item 30a): a record the event is about, e.g. the NCR — opened in a new tab. */
  link?: { href: string; label: string } | null;
}

interface ChangelogProps {
  entries: ChangelogEntry[];
  maxHeight?: string;
  /**
   * Fill the height the card is given instead of stopping at `maxHeight`.
   *
   * Walkthrough batch 3 (Aaron, 17 Sep 2026), on the PO line modal: *"the
   * audit chart only uses half the modal length and as far as I know nothing
   * else goes there so we might as well just use that full length. It looks a
   * bit strange, just randomly cut off halfway through the modal."* The fixed
   * 500px was invisible on the supplier modal, which is shorter, and obvious
   * on the customer one, which has ten more fields down the left.
   *
   * Opt-in, because every other feed that mounts this (Documents, PO header)
   * sits in a page where an unbounded list would push the rest off screen.
   */
  fill?: boolean;
  emptyMessage?: string;
  /**
   * Which grain's rows these are. The schedule modals pass `schedule` and keep
   * the words and formatting they had before CR-7408 rework (GUIDE, 5 Oct 2026:
   * schedule lines are left alone).
   */
  grain?: 'line' | 'schedule';
}

export default function Changelog({ entries, maxHeight = '550px', fill = false, emptyMessage = 'No audit history available', grain = 'line' }: ChangelogProps) {
  const isSchedule = grain === 'schedule';
  const statusWords = isSchedule ? SCHEDULE_ACK_STATUS_DISPLAY : ACK_STATUS_DISPLAY;
  const outcomeWords = isSchedule ? SCHEDULE_DECISION_OUTCOME_LABEL : DECISION_OUTCOME_LABEL;
  const { formatDate: contextFormatDate, formatQuantity } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return contextFormatDate(dateString);
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    // Skip time rendering for date-only strings (YYYY-MM-DD / DD/MM/YYYY)
    // where the 00:00 UTC -> 01:00 BST conversion produces a meaningless
    // "01:00" next to every GRN receipt.
    if (!dateString.includes('T') && !dateString.includes(':')) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const FIELD_LABELS: Record<string, string> = {
    // The CRD column was renamed on 15 Sep 2026; audit rows carry either name.
    customer_required_date: CUSTOMER_REQUIRED_DATE,
    delivery_date: CUSTOMER_REQUIRED_DATE,
    supplier_promise_date: SUPPLIER_PROMISE_DATE,
    // The ODD column was renamed on 14 Sep 2026; audit rows carry either name.
    original_promise_date: ORIGINAL_REQUIRED_DATE,
    original_required_date: ORIGINAL_REQUIRED_DATE,
    acknowledge_status: 'Acknowledge status',
    on_hold: 'Hold status',
    is_critical: 'Critical',
    unit_price: 'Unit price',
    quantity: 'Quantity',
    workflow_status: 'GRN status',
    status: 'Status',
    ncr_raised: 'NCR raised',
    // CR-6215: lifecycle events synthesised into the PO-line audit history.
    grn_created: 'GRN Created',
    delivery_appeal: 'Delivery Appeal',
    // CR-7778: an upload row that was not applied, with the reason.
    upload_skipped: 'Upload skipped',
    // CR-7418 (Aaron, 19 Sep 2026): the fallback sentence-cases the column
    // name, which turns an ACRONYM into a word — "Otif overridden". OTIF is
    // the customer's own term for the measure and is never written like that
    // anywhere else in the portal.
    otif_overridden: 'OTIF Overridden',
    otif_acknowledged: 'OTIF Acknowledged',
    otif_decision: 'OTIF Review',
    // The two STAGING slots. Their column names say "supplier" but either
    // side writes them — on the schedule grain the CUSTOMER proposes into the
    // same fields (ScheduleWorkflowService.propose_quantity_change /
    // propose_alternative_date). The fallback below sentence-cases the column,
    // so a customer's request was headed "Supplier proposed qty" directly
    // above its own reason text, "Customer proposed quantity change: 24" —
    // the row contradicted itself. The reason already names the raiser, so the
    // heading states the field neutrally.
    // Kept in step with purchasing/changelogFields.ts::changeFieldLabel, which
    // is the same map for the PO-line surfaces.
    supplier_proposed_qty: 'Proposed Qty',
    supplier_proposed_date: 'Proposed Date',
  };

  const EVENT_TYPE_CONFIG: Record<string, { label: string; badgeClass: string; dotClass: string; Icon: React.ComponentType<{ className?: string }> }> = {
    grn: { label: 'GRN', badgeClass: 'bg-blue-100 text-blue-800', dotClass: 'bg-blue-500', Icon: Package },
    // CR-6215: delivery-appeal lifecycle events surfaced in the PO-line audit history.
    appeal: { label: 'Appeal', badgeClass: 'bg-indigo-100 text-indigo-800', dotClass: 'bg-indigo-500', Icon: Gavel },
    // CR-7778: a skipped upload attempt (who tried to set what, and why not).
    upload_skipped: { label: 'Upload Skipped', badgeClass: 'bg-rose-100 text-rose-800', dotClass: 'bg-rose-500', Icon: AlertTriangle },
    asn: { label: 'ASN', badgeClass: 'bg-purple-100 text-purple-800', dotClass: 'bg-purple-500', Icon: FileText },
    ncr: { label: 'NCR', badgeClass: 'bg-red-100 text-red-800', dotClass: 'bg-red-600', Icon: AlertTriangle },
    hold: { label: 'Hold', badgeClass: 'bg-orange-100 text-orange-800', dotClass: 'bg-orange-500', Icon: Pause },
    change_request: { label: 'Change Request', badgeClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500', Icon: Edit3 },
    change_accepted: { label: 'Change Accepted', badgeClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500', Icon: CheckCircle },
    // EXPEDITING-PARITY PR 2, walkthrough batch 3 (Aaron, 17 Sep 2026): the
    // supplier answering a NEW order is an ACKNOWLEDGEMENT, not a change
    // decision — *"it presents itself as a change accepted but that kind of
    // implies that they're accepting a change."* Nothing was proposed, so
    // there is no change to accept. Legacy logs it under its own source
    // (NewOrderAcknowledgement). Same green, different words.
    order_acknowledged: { label: 'Order Acknowledged', badgeClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500', Icon: CheckCircle },
    // The backend derives event_type='change_request' for every
    // acknowledge_status row regardless of outcome, so a decline or a
    // withdrawal used to wear the amber "Change Request" badge — the same
    // badge as the proposal it resolved. getEventConfig now narrows those
    // to their real outcome.
    change_declined: { label: 'Change Declined', badgeClass: 'bg-red-100 text-red-800', dotClass: 'bg-red-500', Icon: XCircle },
    change_cancelled: { label: 'Change Cancelled', badgeClass: 'bg-slate-100 text-slate-700', dotClass: 'bg-slate-500', Icon: Ban },
    // EXPEDITING-3 (Aaron, 30 Sep 2026): the declined side closing its request.
    // Nothing is agreed, so it must not read as "Change Accepted".
    request_closed: { label: 'Request Closed', badgeClass: 'bg-slate-100 text-slate-700', dotClass: 'bg-slate-500', Icon: Ban },
    // CR-5329 round 2: document-acceptance event types. Distinct from
    // change_accepted so the badge label reads correctly on the
    // Documents audit feed ("Read & Accepted" rather than the
    // PO-line-flavoured "Change Accepted").
    document_accepted: { label: 'Read & Accepted', badgeClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500', Icon: CheckCircle },
    document_acceptance_enabled: { label: 'Read & Accept Enabled', badgeClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500', Icon: PlusCircle },
    document_notification_sent: { label: 'Notification Sent', badgeClass: 'bg-blue-100 text-blue-800', dotClass: 'bg-blue-500', Icon: Edit3 },
    // CR-5858: company-level "Read & Understood" confirmation. Distinct
    // from document_accepted (per-user) — labelled "Read & Understood" so
    // the audit feed makes clear which workflow fired.
    document_company_confirmed: { label: 'Read & Understood', badgeClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-600', Icon: CheckCircle },
    created: { label: 'Created', badgeClass: 'bg-slate-100 text-slate-700', dotClass: 'bg-slate-500', Icon: PlusCircle },
    // CR-7418 (Aaron, 19 Sep 2026): *"OTIF ack should have its purple colour
    // like the badge"* — and behind that, the real ask: an audit row should
    // wear the colour of the thing it is about, so the timeline and the badges
    // teach the same vocabulary.
    //
    // The CHIP is the category, exactly as GRN / NCR / ASN / Appeal already
    // work — the row's own title already says which of the three happened, and
    // the previous behaviour put "OTIF Overridden [Change Accepted]" on one
    // line, saying two different things about one event. The DOT and the ICON
    // carry the state, in the decision's own colours: teal Overridden,
    // violet Acknowledged, amber back to Not Decided.
    //
    // Worth knowing: violet here sits one hue from the ASN purple above. They
    // never appear in the same story on a PO line in practice, and matching
    // the badge won over avoiding a neighbour, but if ASN ever moves this is
    // the pair to separate.
    otif_overridden: { label: 'OTIF', badgeClass: 'bg-teal-100 text-teal-800', dotClass: 'bg-teal-500', Icon: ShieldCheck },
    otif_acknowledged: { label: 'OTIF', badgeClass: 'bg-violet-100 text-violet-800', dotClass: 'bg-violet-500', Icon: CheckCheck },
    otif_reset: { label: 'OTIF', badgeClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500', Icon: RotateCcw },
    field_change: { label: '', badgeClass: '', dotClass: 'bg-gray-400', Icon: Edit3 },
  };

  const formatFieldName = (field: string) => {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };


  /** ISO date, exactly — not a timestamp, not a longer string containing one. */
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  /** ISO dates embedded anywhere in free text. */
  const ISO_DATE_ANYWHERE = /\d{4}-\d{2}-\d{2}/g;

  const formatEntryValue = (field: string, value: string): string => {
    if (field === 'acknowledge_status') return statusWords[value] || value;
    // CR-8276: a stored quantity ('120.000') reads like the line form ('120').
    const asQuantity = formatQuantityValue(field, value, formatQuantity);
    if (asQuantity !== value) return asQuantity;
    // Date values arrived from the API as ISO and were rendered raw, so a UK
    // user read "2026-09-07 -> 2026-09-09" under a timestamp already shown as
    // 03/09/2026. Matched on shape rather than a list of date fields, so a
    // date field added later is formatted without anyone remembering to.
    if (ISO_DATE.test(value)) return contextFormatDate(value);
    // CR-8276 rework: a stored sentence such as the PO-line-created summary
    // ("… · qty 120.000 · @ 74.680 · …") names its quantity; only that
    // number is reformatted, the price beside it is not.
    return formatQuantityMentions(value, formatQuantity);
  };

  /** The backend bakes ISO dates into the free-text reason ("Supplier proposed
   *  alternative date: 2026-09-09 (was 2026-09-07)"). Those strings are already
   *  stored, so history is rewritten on display rather than on write — which
   *  also means existing rows read correctly, not just new ones. */
  const formatReason = (reason: string): string =>
    // CR-8276: a quantity reason's stored "39.000" reads "39"; a price in a
    // unit-price reason keeps its own decimals (see quantityText.ts).
    formatStoredQuantities(reason.replace(ISO_DATE_ANYWHERE, (m) => contextFormatDate(m)), formatQuantity);

  /* ---------------------------------------------------------------- */
  /*  Decision rows: title by the field that was decided               */
  /* ---------------------------------------------------------------- */
  // Accept / decline / cancel are all logged against `acknowledge_status`,
  // so the audit card was titled "Acknowledge status" no matter which field
  // the decision resolved. The decision row shares a change_request_ref with
  // the proposal row, and the proposal row carries the real field — map ref
  // to field so the decision can borrow it ("Supplier promise date").
  const proposalFieldByRef = useMemo(() => {
    const map = new Map<number, string>();
    for (const entry of entries) {
      if (entry.change_request_ref == null) continue;
      if (entry.field_changed === 'acknowledge_status') continue;
      if (!map.has(entry.change_request_ref)) map.set(entry.change_request_ref, entry.field_changed);
    }
    return map;
  }, [entries]);

  // Fallback for rows predating change_request_ref: the backend builds the
  // reason as "<label> change rejected/cancelled...", so the leading label
  // recovers the field.
  const REASON_LABEL_TO_FIELD: Record<string, string> = {
    'promise date': 'supplier_promise_date',
    'required date': 'customer_required_date',
    'delivery date': 'customer_required_date',
    quantity: 'quantity',
    'unit price': 'unit_price',
  };

  const getDecidedField = (entry: ChangelogEntry): string | null => {
    if (entry.field_changed !== 'acknowledge_status') return null;
    if (entry.change_request_ref != null) {
      const field = proposalFieldByRef.get(entry.change_request_ref);
      if (field) return field;
    }
    const match = (entry.reason || '').match(/^(.+?)\s+change\s+(rejected|declined|cancelled|accepted)/i);
    if (match) return REASON_LABEL_TO_FIELD[match[1].trim().toLowerCase()] || null;
    return null;
  };

  const parseDate = (dateStr: string): Date | null => {
    // Try ISO format first (YYYY-MM-DD)
    let d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
    // Try DD/MM/YYYY format
    const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (parts) {
      d = new Date(parseInt(parts[3]), parseInt(parts[2]) - 1, parseInt(parts[1]));
      if (!isNaN(d.getTime())) return d;
    }
    return null;
  };

  const getDateInterval = (oldDate: string | null, newDate: string) => {
    if (!oldDate || !newDate) return '';

    const old = parseDate(oldDate);
    const newD = parseDate(newDate);
    if (!old || !newD) return '';

    const diffTime = newD.getTime() - old.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Same day';
    if (diffDays > 0) return `+${diffDays} days`;
    return `${diffDays} days`;
  };

  return (
    <div className={`card card-custom h-full${fill ? ' flex flex-col' : ''}`}>
      <div className="card-header border-b px-4 py-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Audit History
        </h3>
      </div>
      <div className={`card-body p-4${fill ? ' flex-1 min-h-0 flex flex-col' : ''}`}>
        {entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <div
            className={`timeline-container${fill ? ' flex-1 min-h-0' : ''}`}
            style={fill ? { overflowY: 'auto' } : { overflowY: 'auto', maxHeight }}
          >
            <div className="space-y-6">
              {entries.map((entry, index) => {
                const eventKey = getEventKey(entry);
                const eventConfig = EVENT_TYPE_CONFIG[eventKey] || EVENT_TYPE_CONFIG['field_change'];
                const decidedField = getDecidedField(entry);
                // Declining or withdrawing one field while other proposals on
                // the same line are still open leaves acknowledge_status
                // untouched, so the row logs the same value on both sides and
                // rendered as "Proposed by Supplier → Proposed by Supplier".
                // The recorded destination is the nonsense part, not the row —
                // which side proposed and what was decided are both what a
                // reader needs — so the outcome the badge already identifies
                // is substituted for the unmoved status.
                const isNoOpTransition = !!entry.old_value && entry.old_value === entry.new_value;
                const decisionOutcome = outcomeWords[eventKey];
                return (
                <div key={entry.id || index} className="timeline-item flex gap-3">
                  {/* Timeline Label - Date/Time */}
                  <div className="timeline-label min-w-[100px] text-sm">
                    <div className="font-semibold text-dark">{formatDate(entry.change_date)}</div>
                    <div className="text-muted-foreground">{formatTime(entry.change_date)}</div>
                  </div>

                  {/* Timeline Badge */}
                  <div className="timeline-badge flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full ${eventConfig.dotClass} flex items-center justify-center text-white shadow-sm`}>
                      <eventConfig.Icon className="h-3.5 w-3.5" />
                    </div>
                    {index < entries.length - 1 && (
                      <div className="w-0.5 flex-1 bg-border mt-1"></div>
                    )}
                  </div>

                  {/* Timeline Content */}
                  <div className="timeline-content flex-1 pb-4">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="font-semibold text-dark">
                        {/* CR-4731: Show "Change Accepted" for accepted changes */}
                        {/* CR-5282: 'created' rows are an event, not a field
                            change — the "Created" badge already conveys the
                            kind, so the title reads as the entity that was
                            created instead of duplicating the badge label. */}
                        {entry.field_changed === 'created'
                          ? 'PO Line'
                          : decidedField
                          ? formatFieldName(decidedField)
                          : isClosedRequestRow(entry)
                          ? 'Request Closed'
                          : entry.field_changed === 'acknowledge_status' && entry.new_value === 'accepted'
                          ? (entry.old_value === 'new' ? 'Order Acknowledged' : 'Change Accepted')
                          : formatFieldName(entry.field_changed)}
                      </span>
                      {eventConfig.label && (
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${eventConfig.badgeClass}`}>
                          {eventConfig.label}
                        </span>
                      )}
                    </div>

                    {isClosedRequestRow(entry) ? (
                      <div className="text-dark font-medium">Change Declined → {closedRequestOutcome(entry)}</div>
                    ) : isNoOpTransition ? (
                      <div className="text-dark font-medium">
                        {formatEntryValue(entry.field_changed, entry.new_value)}
                        {decisionOutcome ? ` → ${decisionOutcome}` : ''}
                      </div>
                    ) : entry.old_value && entry.new_value ? (
                      <>
                        <div className="text-dark font-medium">
                          {formatEntryValue(entry.field_changed, entry.old_value)} → {formatEntryValue(entry.field_changed, entry.new_value)}
                        </div>
                        {entry.field_changed.toLowerCase().includes('date') && (
                          <div className="text-muted-foreground text-sm">
                            ({getDateInterval(entry.old_value, entry.new_value)})
                          </div>
                        )}
                      </>
                    ) : entry.new_value ? (
                      <div className={`text-dark ${entry.event_type === 'grn' ? 'whitespace-pre-line text-sm leading-relaxed' : 'font-medium'}`}>
                        {/* CR-7408 rework: a date set where there was none skipped the formatter and read ISO. */}
                        {entry.event_type === 'grn'
                          ? formatQuantityTokens(entry.new_value, formatQuantity)
                          : isSchedule ? entry.new_value : formatEntryValue(entry.field_changed, entry.new_value)}
                      </div>
                    ) : null}

                    {entry.link && (
                      <a
                        href={entry.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {entry.link.label}
                      </a>
                    )}

                    {/* Inline fill bar for GRN receipts — shows the schedule
                        filling up over time. Parses "X of Y" from the new_value
                        text, e.g. "3.000 of 8.000". */}
                    {entry.event_type === 'grn' && entry.new_value && (() => {
                      const match = entry.new_value.match(/([0-9.]+)\s+of\s+([0-9.]+)/);
                      if (!match) return null;
                      const filled = parseFloat(match[1]);
                      const total = parseFloat(match[2]);
                      if (!total || total <= 0) return null;
                      const pct = Math.min(100, (filled / total) * 100);
                      return (
                        <div className="mt-2 w-full max-w-xs h-2 bg-gray-100 rounded overflow-hidden border border-gray-200">
                          <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      );
                    })()}

                    {entry.reason && (
                      <div className="text-muted-foreground text-sm mt-1">
                        {formatReason(entry.reason)}
                      </div>
                    )}

                    {/* CR-4731 round 5: surface the submission event when
                        the audit row was collapsed from a propose+decision
                        pair. Renders an italicised "Submitted by ..." sub-
                        line so the lifecycle is visible in a single card,
                        keeping the round 3 "1 entry per change request"
                        invariant. */}
                    {entry.submission_event && (
                      <div className="text-muted-foreground text-xs mt-1 italic">
                        Submitted {formatDate(entry.submission_event.change_date)} {formatTime(entry.submission_event.change_date)} by{' '}
                        {entry.submission_event.changed_by_company
                          ? `${entry.submission_event.changed_by_company} · `
                          : ''}
                        {entry.submission_event.changed_by}
                      </div>
                    )}

                    <div className="text-muted-foreground text-sm mt-1 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {entry.changed_by_company ? `${entry.changed_by_company} · ` : ''}{entry.changed_by}
                      {/* EXPEDITING-3 (Aaron, 30 Sep 2026): changed_by already names the
                          person who actually made the change (changeActorName), as
                          legacy does; the company stays the one they acted as. */}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
